import devConfig from '@/devconfig.js';
import devPages from '@/devpages.js';
//import Vue from 'vue';
import axios from 'axios';
//import ms from 'ms';
//import moment from 'moment';
import merge from 'lodash/merge';
import assign from 'lodash/assign';
import filter from 'lodash/filter';
import P from 'bluebird';
import xmlParser from '@/services/xmlParser.js';

const defaultBaseUrl = 'https://ott.gideo.video/api/legacy';
const settingsBaseURL = devConfig?.settingsBaseURL || defaultBaseUrl;
const domain = devConfig?.domain || global.location.host;

function getAPIError(e, defaultMessage, handlers = {}) {
    defaultMessage = defaultMessage || 'An unknown error occurred';

    const errorMessage = e.response?.data?.error || defaultMessage;
    const userMessage = e.response?.data?.message || defaultMessage;

    const status = e.response?.status;

    const hError =
        typeof handlers[status] === 'function'
            ? handlers[status](e.response?.data, e)
            : undefined;

    const errorData = {
        code: 'APIERROR',
        response: e.response,
        data: e.response?.data,
        status: e.response?.status,
        userMessage: userMessage,
    };

    return assign(new Error(errorMessage), errorData, hError);
}

function getToken() {
    return localStorage.getItem('gideo-token');
}

function setToken(t) {
    localStorage.setItem('gideo-token', t);
}

function setUser(u) {
    localStorage.setItem('gideo-user', JSON.stringify(u));
}

function getUser() {
    let user = localStorage.getItem('gideo-user');
    return JSON.parse(user) || {};
}

function logout(reason, bus) {
    localStorage.removeItem('gideo-token');
    localStorage.removeItem('gideo-user');
    bus.$emit('logged-out', { reason });
}

function getRequestFunction(base, bus) {
    return async function (uri, options, asyncTransformer) {
        const token = getToken();

        options = options || {};
        options.base = options.base || base;
        options.base = await options.base;

        const request = merge(
            {
                url: `${options.base}${uri}`,

                headers:
                    token === null || !options.sendToken
                        ? undefined
                        : {
                              authorization: `Bearer ${token}`,
                          },
            },
            options
        );

        return P.resolve(
            axios.request(request).then((response) => {
                if (typeof asyncTransformer === 'function') {
                    return asyncTransformer(response.data).then(
                        (parsedData) => {
                            response.data = parsedData;
                            return response;
                        }
                    );
                }

                return response;
            })
        )
            .tapCatch((err) => {
                if (err.response && err.response.status === 401) {
                    logout(getToken() !== null ? 'expired' : 'required', bus);
                }
            })
            .catch((err) => {
                // If the server provides an error message in the payload,
                // construct a new error using that as the text for
                // convenience.
                const errorText = err.response?.data?.error;

                return P.reject(
                    errorText
                        ? assign(new Error(errorText), {
                              response: err.response,
                          })
                        : err
                );
            });
    };
}

async function getSettings() {
    return getRequestFunction(settingsBaseURL)(
        `?cmd=getSettings&domain=${domain}`,
        { headers: { accept: 'application/json' } }
    );
}

const settings = getSettings().then((data) => {
    const settings = data.data;
    const appConfig = devConfig
        ? merge({}, settings.appConfig?.webtv || {}, devConfig)
        : settings.appConfig?.webtv;

    appConfig.assetsURL = appConfig.assetsURL || '/assets/';

    function prefixUrl(url, prefix) {
        return /^(https?:\/\/|data:image|\/)/i.test(url)
            ? url
            : `${prefix}${url}`;
    }

    function adjustUrl(key, def) {
        const value = appConfig[key];

        if (!value) {
            appConfig[key] = undefined;
        } else {
            appConfig[key] = prefixUrl(
                value === true ? def : value,
                appConfig.assetsURL
            );
        }
    }

    adjustUrl('backgroundImage', 'background.png');
    adjustUrl('cssURL', 'main.css');

    //white label logo
    if (!appConfig.whiteLabelLogo) {
        appConfig.whiteLabelLogo = [
            {
                url: 'https://gideo.video',
                image: require('@/assets/powered-by-gideo.png'),
            },
        ];
    }
    if (!Array.isArray(appConfig.whiteLabelLogo)) {
        appConfig.whiteLabelLogo = [appConfig.whiteLabelLogo];
    }

    appConfig.whiteLabelLogo.forEach((wll) => {
        if (!wll.image) {
            wll.image = require('@/assets/powered-by-gideo.png');
            wll.url = 'https://gideo.video';
        } else {
            wll.image = prefixUrl(wll.image, appConfig.assetsURL);
        }
    });

    appConfig.logo = prefixUrl(
        appConfig.logo || 'logo.png',
        appConfig.assetsURL
    );

    merge(settings, appConfig);
    delete settings.appConfig;

    settings.vastUrl =
        typeof settings.vastUrl === 'string'
            ? settings.vastUrl
            : settings.vastUrl?.webtv;

    return settings;
});
const accountId = settings.then((v) => v.accountId);

// apiBaseUrls properties are possibly overridden when gideo.getSettings is called
const apiBaseUrls = {
    ott: settings.then((s) => s.apiBaseUrls.ott || defaultBaseUrl),
    auth: settings.then((s) => s.apiBaseUrls.auth || defaultBaseUrl),
    store: settings.then((s) => s.apiBaseUrls.store || defaultBaseUrl),
    analytics: settings.then((s) => s.apiBaseUrls.analytics || ''),
};

export default {
    install(Vue, { base } = { base: apiBaseUrls.ott }) {
        const bus = new Vue();
        const request = getRequestFunction(base, bus);

        const self = {
            bus,

            get authed() {
                return getToken() !== null;
            },

            get subscriber() {
                let user = getUser();
                return !!user?.subscriber;
            },

            async getPageContent(id) {
                if (process.env.NODE_ENV === 'development') {
                    if (devPages[id]) {
                        return devPages[id];
                    }
                }
                return (
                    await axios.get(`/rawpage/${id}.html`, {
                        responseType: 'text',
                    })
                ).data;
            },

            async login(o) {
                let aId = await accountId;
                o = merge(o, {
                    cmd: 'authenticate',
                    accountId: aId,
                    source: 'web',
                });

                return request('', {
                    method: 'post',
                    data: o,
                    base: apiBaseUrls.auth,
                    sendToken: true,
                })
                    .then(({ data }) => {
                        setToken(data.deviceToken);
                        setUser({
                            id: data.userId,
                            subscriber: data.subscriber,
                        });
                        self.bus.$emit('logged-in');
                    })
                    .catch((e) => {
                        let status = e.response?.status;

                        if (status === 401) {
                            return P.reject(
                                Object.assign(
                                    new Error('Invalid Credentials'),
                                    { code: 'EKEYREJECTED' }
                                )
                            );
                        } else {
                            return P.reject(
                                Object.assign(new Error('Unknown Error'), {
                                    code: '',
                                    error: e,
                                })
                            );
                        }
                    });
            },

            logout() {
                return P.try((reason) => logout(reason, this.bus));
            },

            async resetPassword(o) {
                const aId = await accountId;
                o = merge(o, { cmd: 'reset_password', accountId: aId });

                return request('', {
                    method: 'post',
                    data: o,
                    base: apiBaseUrls.auth,
                    sendToken: true,
                }).catch((e) => {
                    return P.reject(
                        Object.assign(new Error('Unknown Error'), {
                            code: '',
                            error: e,
                        })
                    );
                });
            },

            async getSuggestedContent(id) {
                return this.getProgramGroups(id, true);
            },

            async getProgramGroups(id, isSuggestedContent) {
                if (!id) {
                    id = '';
                }
                let user = getUser();
                let aId = await accountId;

                return request(
                    `?cmd=getCategoryChildren&AccountID=${aId}&CategoryID=${id}`,
                    {
                        sendToken: !!isSuggestedContent,
                    },
                    (data) =>
                        xmlParser.parseProgramItems(data).then((data) => {
                            //remove all items hidden for this user
                            const behavior = user?.subscriber
                                ? 'subscriberBehavior'
                                : 'freeBehavior';
                            return filter(
                                data,
                                (item) => item[behavior] !== 'hide'
                            );
                        })
                );
            },

            async getProgramGroup(id) {
                if (!id) {
                    id = '';
                }
                let user = getUser();
                let aId = await accountId;

                return request(
                    `?cmd=getCategory&AccountID=${aId}&CategoryID=${id}`,
                    {},
                    (data) =>
                        xmlParser.parseProgramItems(data).then((parsedData) => {
                            const behavior = user?.subscriber
                                ? 'subscriberBehavior'
                                : 'freeBehavior';

                            parsedData = filter(
                                parsedData,
                                (item) => item[behavior] !== 'hide'
                            );

                            return parsedData && parsedData[0];
                        })
                );
            },

            async getProgram(id) {
                if (!id) {
                    id = '';
                }
                let user = getUser();
                let aId = await accountId;

                return request(
                    `?cmd=getVideo&AccountID=${aId}&VideoID=${id}`,
                    {},
                    (data) =>
                        xmlParser.parseProgramItems(data).then((parsedData) => {
                            const behavior = user?.subscriber
                                ? 'subscriberBehavior'
                                : 'freeBehavior';
                            parsedData = filter(
                                parsedData,
                                (item) => item[behavior] !== 'hide'
                            );
                            return parsedData && parsedData[0];
                        })
                );
            },

            async getProgramURLs(id) {
                if (!id) {
                    id = '';
                }
                let aId = await accountId;

                return request(
                    `?cmd=getVideoUrls&accountId=${aId}&videoId=${id}`,
                    {
                        sendToken: true,
                    }
                );
            },

            async search(term) {
                let user = getUser();
                let aId = await accountId;

                return request(
                    `?cmd=getVideos&AccountID=${aId}&Search=${term}`,
                    {},
                    (data) =>
                        xmlParser.parseProgramItems(data).then((data) => {
                            //remove all items hidden for this user
                            const behavior = user?.subscriber
                                ? 'subscriberBehavior'
                                : 'freeBehavior';
                            return filter(
                                data,
                                (item) => item[behavior] !== 'hide'
                            );
                        })
                );
            },

            async getTermsOfUse() {
                let aId = await accountId;

                return request(
                    `?cmd=getTermsOfUse&AccountID=${aId}`,
                    {},
                    (data) => xmlParser.parseTerms(data)
                );
            },
            async getPrivacyPolicy() {
                let aId = await accountId;

                return request(
                    `?cmd=getPrivacyPolicy&AccountID=${aId}`,
                    {},
                    (data) => xmlParser.parseTerms(data)
                );
            },
            async getUserInfo() {
                return request('?cmd=getUser', {
                    base: apiBaseUrls.auth,
                    sendToken: true,
                })
                    .then((user) => {
                        user = user.data;
                        setUser(user);
                        return user;
                    })
                    .catch(() => {
                        return undefined;
                    });
            },
            async getUserSubscriptions() {
                return (
                    getUser()?.subscriptions ||
                    (await this.getUserInfo())?.subscriptions
                );
            },
            async checkGiftExists(code) {
                let aId = await accountId;

                let data = {
                    accountId: aId,
                    code,
                    trial_run: true,
                };

                return request(`?cmd=redeem`, {
                    base: apiBaseUrls.store,
                    data,
                    method: 'post',
                })
                    .then(() => true)
                    .catch((e) => {
                        let status = e?.response?.status;
                        if (status === 404) {
                            return false;
                        }
                        return true;
                    });
            },
            async redeemGift(code, creds = {}) {
                const aId = await accountId;

                const { email, password } = creds;
                const data = {
                    cmd: 'redeem',
                    source: 'web',
                    accountId: aId,
                    email,
                    password,
                    code,
                };

                return request('', {
                    base: apiBaseUrls.store,
                    data,
                    method: 'post',
                    sendToken: true,
                })
                    .then(({ data }) => {
                        const wasAuthed = this.authed;
                        const user = getUser() || {};
                        user.subscriber = true;
                        user.id = data.id;
                        setUser(user);
                        setToken(data.token);
                        if (!wasAuthed) {
                            self.bus.$emit('logged-in');
                        }
                        self.bus.$emit('subscribed');
                        self.bus.$emit('userUpdated', user);
                    })
                    .catch((e) => {
                        const errorText =
                            e.response?.data?.error ||
                            'An unknown error occurred while trying to redeem a gift.';
                        const message =
                            e.response?.data?.errorMessage ||
                            'Something went wrong. Please try again later.';

                        return P.reject(
                            assign(new Error(errorText), {
                                response: e.response,
                                message,
                            })
                        );
                    });
            },
            async checkSignupFields(email, customAnswers) {
                return this.createUser(email, undefined, customAnswers, true);
            },
            async createUser(
                email,
                password,
                customAnswers,
                trial_run = false
            ) {
                let aId = await accountId;

                let data = {
                    cmd: 'signup',
                    source: 'web',
                    accountId: aId,
                    email,
                    password,
                    customAnswers,
                    trial_run,
                };

                return request('', {
                    base: apiBaseUrls.store,
                    data,
                    method: 'post',
                })
                    .then(async ({ data }) => {
                        if (trial_run) {
                            return true;
                        }
                        setToken(data.token);
                        setUser({
                            id: data.userId,
                            subscriber: false,
                        });
                        await this.getUserInfo();
                        self.bus.$emit('logged-in');
                        self.bus.$emit('userUpdated', getUser());
                    })
                    .catch((e) => {
                        const errorText =
                            e.response?.data?.error ||
                            'An unknown error occurred while trying to create a new user account';

                        return P.reject(
                            errorText
                                ? assign(new Error(errorText), {
                                      response: e.response,
                                      userMessage:
                                          e.response?.data?.message ||
                                          'An unknown error occurred. Please try again later.',
                                      field: e.response?.data?.field,
                                  })
                                : e
                        );
                    });
            },
            async donate(amount, card, message, email) {
                const data = {
                    cmd: 'purchase',
                    purchase: {
                        productId: 'donate',
                        amount,
                        email,
                        message,
                    },
                    card,
                };
                if (!this.authed) {
                    data.accountId = await accountId;
                }
                return request('', {
                    base: apiBaseUrls.store,
                    data,
                    method: 'post',
                    sendToken: true,
                });
            },
            async purchaseSubscription(productId, card, giftDetails) {
                let purchase = { productId };
                if (giftDetails) {
                    purchase = merge(purchase, giftDetails, { isGift: true });
                }

                const data = {
                    cmd: 'purchase',
                    purchase,
                    card,
                    source: 'web',
                };
                if (!this.authed) {
                    data.accountId = await accountId;
                }
                return request('', {
                    base: apiBaseUrls.store,
                    data,
                    method: 'post',
                    sendToken: true,
                })
                    .then((res) => {
                        if (!giftDetails) {
                            setToken(res.data.token);
                            const user = getUser() || {};
                            user.subscriber = true;
                            setUser(user);
                            self.bus.$emit('subscribed');
                            self.bus.$emit('userUpdated', getUser());
                        }
                        return res;
                    })
                    .catch((e) => {
                        const errorText =
                            e.response?.data?.error ||
                            'An unknown error occurred while trying to make a purchase.';
                        const message =
                            e.response?.data?.errorMessage ||
                            'Something went wrong. Please try again later.';

                        return P.reject(
                            assign(new Error(errorText), {
                                response: e.response,
                                message,
                            })
                        );
                    });
            },
            async changePassword(data) {
                data = Object.assign({ cmd: 'change_password' }, data);
                return request('', {
                    base: apiBaseUrls.auth,
                    data,
                    method: 'post',
                }).catch((e) => {
                    return P.reject(
                        getAPIError(
                            e,
                            'An unknown error occurred while trying to update your password.',
                            {
                                401: () => ({
                                    message: 'Token Expired',
                                    code: 'EKEYEXPIRED',
                                    userMessage:
                                        'This password reset token has expired',
                                }),
                            }
                        )
                    );
                });
            },
            async updateUser(newUser, currentPassword) {
                let data = {
                    cmd: 'update_user',
                    user: newUser,
                };
                if (currentPassword) {
                    data.currentPassword = currentPassword;
                }
                return request('', {
                    base: apiBaseUrls.auth,
                    data,
                    method: 'post',
                    sendToken: true,
                })
                    .then((res) => {
                        const user = getUser() || {};
                        delete newUser.password;
                        setUser(Object.assign(user, newUser));
                        self.bus.$emit('userUpdated', getUser());
                        return res;
                    })
                    .catch((e) => {
                        const errorText =
                            e.response?.data?.message ||
                            e.response?.data?.error ||
                            'An unknown error occurred while trying to update account details.';

                        return P.reject(
                            errorText
                                ? assign(new Error(errorText), {
                                      response: e.response,
                                  })
                                : e
                        );
                    });
            },
            async updatePaymentMethod(card) {
                return request('', {
                    base: apiBaseUrls.store,
                    data: {
                        cmd: 'updatePaymentMethod',
                        card,
                    },
                    method: 'post',
                    sendToken: true,
                })
                    .then(async (res) => {
                        await this.getUserInfo();
                        self.bus.$emit('userUpdated', getUser());
                        return res;
                    })
                    .catch((e) => {
                        const errorText =
                            e.response?.data?.error ||
                            'An unknown error occurred while trying to update payment method.';

                        return P.reject(
                            errorText
                                ? assign(new Error(errorText), {
                                      response: e.response,
                                  })
                                : e
                        );
                    });
            },
            async upgradeSubscription(transactionId, productId) {
                return request('', {
                    base: apiBaseUrls.store,
                    data: {
                        cmd: 'upgrade',
                        source: 'web',
                        transactionId,
                        productId,
                    },
                    method: 'post',
                    sendToken: true,
                })
                    .then(async (res) => {
                        await this.getUserInfo();
                        self.bus.$emit('userUpdated', getUser());
                        return res;
                    })
                    .catch((e) => {
                        const errorText =
                            e.response?.data?.error ||
                            'An unknown error occurred while trying to renew subscription.';

                        return P.reject(
                            errorText
                                ? assign(new Error(errorText), {
                                      response: e.response,
                                  })
                                : e
                        );
                    });
            },
            async renewSubscription(transactionId) {
                return request('', {
                    base: apiBaseUrls.store,
                    data: {
                        cmd: 'renewSubscription',
                        source: 'web',
                        transactionId,
                    },
                    method: 'post',
                    sendToken: true,
                })
                    .then(async (res) => {
                        setToken(res.data.token);
                        await this.getUserInfo();
                        self.bus.$emit('userUpdated', getUser());
                        return res;
                    })
                    .catch((e) => {
                        const errorText =
                            e.response?.data?.error ||
                            'An unknown error occurred while trying to renew subscription.';

                        return P.reject(
                            errorText
                                ? assign(new Error(errorText), {
                                      response: e.response,
                                  })
                                : e
                        );
                    });
            },
            async cancelSubscription(transactionId) {
                return request('', {
                    base: apiBaseUrls.store,
                    data: {
                        cmd: 'cancelSubscription',
                        source: 'web',
                        transactionId,
                    },
                    method: 'post',
                    sendToken: true,
                })
                    .then(async (res) => {
                        await this.getUserInfo();
                        self.bus.$emit('userUpdated', getUser());
                        return res;
                    })
                    .catch((e) => {
                        const errorText =
                            e.response?.data?.error ||
                            'An unknown error occurred while trying to cancel subscription.';

                        return P.reject(
                            errorText
                                ? assign(new Error(errorText), {
                                      response: e.response,
                                  })
                                : e
                        );
                    });
            },
            async getSubscriptions(options) {
                options = options || {};
                const aId = await accountId;
                return request(
                    `?cmd=getSubscriptionProducts&accountId=${aId}&platform=web&giftable=${!!options.giftable}`,
                    {
                        base: apiBaseUrls.store,
                        sendToken: true,
                    }
                ).then((subscriptions) => subscriptions.data);
            },
            async getProductUpgrades(productId) {
                const aId = await accountId;
                return request(
                    `?cmd=getProductUpgrades&accountId=${aId}&productId=${productId}`,
                    {
                        base: apiBaseUrls.store,
                        sendToken: true,
                    }
                ).then((subscriptions) => subscriptions.data);
            },
        };

        Vue.prototype.$gideo = self;
        //this will load site settings and refresh the user's token
        const refreshToken = () => {
            if (getToken()) {
                return self.login().catch(() => false);
            }
        };
        Vue.prototype.$appSettings = Promise.all([
            settings,
            refreshToken(),
        ]).then(([settings]) => {
            self.bus.$emit('settings-loaded', { settings });
            return settings;
        });
    },
};
